import Page from "~/components/Page";
import React, { useEffect, useMemo, useState } from "react";
import docStore from "~/stores/document";
import { DocRelation, Document, DocVersion } from "~/document";
import { Button } from "~/components/ui/button";
import { Badge } from "~/components/ui/badge";
import { Spinner } from "~/components/ui/spinner";
import { Skeleton } from "~/components/ui/skeleton";
import { Empty, EmptyHeader, EmptyTitle, EmptyDescription } from "~/components/ui/empty";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Check } from "lucide-react";
import { observer } from "mobx-react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import paths from "~/paths";
import DocumentPreview from "~/components/DocumentPreview";
import ShareDocumentButton from "~/components/DocumentShareButton";
import DocumentDownloadButton from "~/components/DocumentDownloadButton";
import { NotFound, ReqStatus } from "~/api";
import BackToSearchResultsButton from "~/components/BackToSearchResultsButton";
import DocBookmarkButton from "~/components/DocBookmarkButton";
import moment from "moment";
import { PUB_DATE_FORMAT } from "~/constants";
import { formatBytes, getFullDocTypes } from "~/util";
import docSeriesStore from "~/stores/docRelationTypes";
import languageStore from "~/stores/language";
import DocumentArchiveModal from "~/components/DocumentArchiveModal";

interface IInfoItem {
  text: string;
  content?: React.ReactNode;
  innerHTML?: string;
}

interface DocumentInfoProps {
  document: Document;
}

export const DocumentDetailPageRedirect = () => {
  const { id } = useParams();
  return <Navigate to={paths.construct.docDetail(id)} />;
};

const InfoItem = ({ item }: { item: IInfoItem }) => {
  return (
    <div className="mb-[18px] text-sm leading-none">
      <div className="font-semibold">{item.text}</div>
      {!!item.content && <div className="mt-3">{item.content}</div>}
      {!!item.innerHTML && <div className="mt-3" dangerouslySetInnerHTML={{ __html: item.innerHTML }} />}
    </div>
  );
};

const DocumentDeletionModal = observer((props: { document: Document; open: boolean; close: () => void }) => {
  const { id } = useParams();

  const onDelete = async () => {
    await docStore.deleteDocument(props.document.protocol);
  };

  useEffect(() => {
    docStore.documentDeletionStatus = ReqStatus.Initial;
  }, [id]);

  const content = (() => {
    switch (docStore.documentDeletionStatus) {
      case ReqStatus.Initial: {
        return (
          <div className="space-y-2 text-base">
            <p className="font-semibold">
              A GoDocs document should be deleted only if it was uploaded by mistake and the link was never shared.
            </p>
            <p>Existing GoDocs documents should be updated or archived.</p>
          </div>
        );
      }
      case ReqStatus.InProcess: {
        return (
          <div className="flex items-center gap-2">
            <Spinner className="size-5" />
            <p className="text-base">Deleting document...</p>
          </div>
        );
      }
      case ReqStatus.Success: {
        return (
          <div className="flex items-center gap-2">
            <Check className="size-6 text-success-600" />
            <p className="text-base">Successfully deleted the document</p>
          </div>
        );
      }
      case ReqStatus.Failed: {
        return (
          <div className="flex items-center gap-2">
            <p className="text-base">Failed to delete the document</p>
          </div>
        );
      }
      default: {
        return null;
      }
    }
  })();

  const deletingDocument = docStore.documentDeletionStatus === ReqStatus.InProcess;

  const footer = () => {
    switch (docStore.documentDeletionStatus) {
      case ReqStatus.Initial:
      case ReqStatus.InProcess: {
        return (
          <>
            <Button variant="secondary" onClick={props.close} disabled={deletingDocument}>
              Go Back
            </Button>
            <Button variant="destructive" onClick={onDelete} disabled={deletingDocument}>
              Continue deleting
            </Button>
          </>
        );
      }
      case ReqStatus.Success: {
        return (
          <Link to="/">
            <Button variant="default" onClick={props.close}>
              Done
            </Button>
          </Link>
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.close();
      }}
    >
      <DialogContent className="sm:max-w-[480px]" aria-describedby={undefined}>
        <DialogHeader>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Document management</p>
          <DialogTitle>Delete document</DialogTitle>
        </DialogHeader>
        <div className="min-h-[56px]">{content}</div>
        <DialogFooter>{footer()}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

const DocumentInfo = ({ document }: DocumentInfoProps) => {
  const documentLanguage = docStore.languageStore?.loadedLanguages[document.language]?.text;
  const [versions, setVersions] = useState<DocVersion[]>([]);
  useEffect(() => {
    (async () => {
      const res = await docStore.loadDocumentVersions(document.id);
      setVersions(res);
    })();
  }, []);

  const pdfOptionMapping: any = {
    "0": "PDF Not Allowed",
    "1": "PDF Allowed",
    "3": "PDF Preferred download type",
    "5": "Only allow pdf format",
  };
  const leftCol: IInfoItem[] = [
    {
      text: "Document language",
      content: documentLanguage || null,
    },
    { text: "Topics", content: document.topics_display ? document.topics_display : null },
    { text: "Document types", content: getFullDocTypes(document) },
    { text: "Geographical coverage", content: document.countries_display },
    {
      text: "Organizational Unit",
      content: document.organizational_unit?.name || null,
    },
    { text: "Access", content: document.access_display ? document.access_display : null },
    { text: "Series", content: document.third_type_display ? document.third_type_display : null },
    { text: "Document symbol", content: document.number },
    {
      text: "Publication Date",
      content: document.publication_date ? moment(document.publication_date).format("YYYY-MM-DD") : null,
    },
    { text: "Release Date", content: document.release_date ? document.release_date : null },
    { text: "Expiration Date", content: document.expiration_date ? document.expiration_date : null },
    { text: "Document manager", content: document.owner?.email ? document.owner?.email : null },
  ].filter((item) => !!item.content);
  const rightCol: IInfoItem[] = [
    { text: "Tags", content: document.tags_display ? document.tags_display.join(", ") : null },
    {
      text: "Document history",
      content:
        versions && versions.length !== 0 ? (
          <ul className="ml-4 list-outside list-disc">
            {versions.map((a, idx) => {
              const label = idx === 0 ? "Latest" : `Version ${idx}`;
              const timestamp = moment(a.last_modified_date).format(PUB_DATE_FORMAT);
              return (
                <li key={a.uuid || idx} className="mb-2">
                  <a href={a.file}>{label}</a> <span>({timestamp})</span>
                </li>
              );
            })}
          </ul>
        ) : null,
    },
    {
      text: "Last Modified By",
      content: document.last_modified_user ? document.last_modified_user.email : null,
    },
    {
      text: "File Size",
      content: document.file_info?.size ? formatBytes(parseInt(document.file_info.size)) : null,
    },
    {
      text: "File Type",
      content: document.file_info ? document.file_info.ext : null,
    },
    {
      text: "PDF Option",
      content: document.pdf_option || document.pdf_option === 0 ? pdfOptionMapping[document.pdf_option] : null,
    },
    { text: "Document ID", content: document.protocol },
    { text: "Note", content: document.comment ? document.comment : null },
  ].filter((item) => !!item.content);
  return (
    <div>
      {!!document.trans_group.length && (
        <div className="mt-1.5 mb-5">
          <div className="mb-3 text-sm font-semibold">Document translations</div>
          {document.trans_group.map((langVersion) => (
            <div key={`lang-version-${langVersion.uuid}`} className="mb-1">
              <Badge variant="default" className="mr-3">
                {languageStore.idToText(langVersion.language)}
              </Badge>
              <Link to={paths.construct.docDetail(langVersion.protocol)}>{langVersion.title}</Link>
            </div>
          ))}
        </div>
      )}
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-7">
          {leftCol.map((item) => (
            <InfoItem key={item.text} item={item} />
          ))}
        </div>
        <div className="col-span-5">
          {rightCol.map((item) => (
            <InfoItem key={item.text} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
};

const DocumentRelations = observer(({ related }: { related: DocRelation[] }) => {
  useEffect(() => {
    docSeriesStore.f.load("", null);
  }, []);
  return (
    !!docSeriesStore.f.itemsList.length &&
    !!related.length && (
      <div className="mb-5 flex flex-col gap-[3px]">
        {related.map((relatedDoc) => {
          const relation = docSeriesStore.f.items[relatedDoc.type];
          const relationType = relatedDoc.is_owner ? relation.left_to_right : relation.right_to_left;
          return (
            <div key={relatedDoc.other.protocol} className="flex items-center gap-[5px]">
              <Badge variant="warning">{relationType}</Badge>
              <Link className="no-underline" to={paths.construct.docDetail(relatedDoc.other.protocol)}>
                {relatedDoc.other.title}
              </Link>
            </div>
          );
        })}
      </div>
    )
  );
});

// Placeholder shaped like the loaded detail page (left: back button, title,
// abstract, action buttons, two-column info grid, manage buttons; right: preview).
const DocumentDetailSkeleton = () => (
  <div className="relative mx-auto flex h-full w-full max-w-[1700px] justify-between gap-[30px]">
    <div className="grow-[2]">
      <div className="mb-6">
        <Skeleton className="h-9 w-44 rounded-md" />
      </div>
      <div className="pb-4">
        <Skeleton className="mb-3 h-7 w-2/3" />
        <Skeleton className="mb-1.5 h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
      <div className="mb-5 flex flex-wrap gap-1.5">
        <Skeleton className="h-9 w-24 rounded-md" />
        <Skeleton className="h-9 w-28 rounded-md" />
        <Skeleton className="h-9 w-28 rounded-md" />
      </div>
      <div className="grid max-w-[800px] grid-cols-12 gap-4">
        <div className="col-span-7 flex flex-col gap-[18px]">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i}>
              <Skeleton className="mb-2.5 h-3.5 w-32" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
        <div className="col-span-5 flex flex-col gap-[18px]">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}>
              <Skeleton className="mb-2.5 h-3.5 w-28" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ))}
        </div>
      </div>
      <div className="mt-6 flex flex-wrap gap-1.5">
        <Skeleton className="h-9 w-20 rounded-md" />
        <Skeleton className="h-9 w-32 rounded-md" />
        <Skeleton className="h-9 w-32 rounded-md" />
      </div>
    </div>
    <div className="flex grow justify-end max-lg:hidden">
      <Skeleton className="h-[700px] w-full min-w-[440px] max-w-[550px] rounded-md" />
    </div>
  </div>
);

const DocumentDetailPage = observer(() => {
  const params = useParams();
  const navigate = useNavigate();
  const title = useMemo(() => {
    if (docStore.currentDocument === null) return "Document detail";
    return docStore.currentDocument.title;
  }, [docStore.currentDocument]);

  const [showDocumentDeletionDialog, setShowDocumentDeletionDialog] = useState<boolean>(false);
  const [showDocumentArchiveDialog, setShowDocumentArchiveDialog] = useState<boolean>(false);
  const [showDocumentRepublishDialog, setShowDocumentRepublishDialog] = useState<boolean>(false);
  const startDeletion = () => setShowDocumentDeletionDialog(true);
  const [notFound, setNotFound] = useState(false);
  const proto = params.id!;

  const reloadDoc = () => docStore.selectDocument(proto);

  useEffect(() => {
    (async () => {
      try {
        await reloadDoc();
      } catch (err) {
        if (err instanceof NotFound) {
          setNotFound(true);
        }
      }
    })();
  }, [proto]);

  if (notFound) {
    return (
      <Page title="Document not found">
        <Empty className="m-8">
          <EmptyHeader>
            <EmptyTitle>Not found</EmptyTitle>
            <EmptyDescription>
              Document "{proto}" doesn't exist. <Link to={paths.construct.browse()}>Go to the Home page</Link>
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </Page>
    );
  }

  if (docStore.currentDocument === null || docStore.loadingDocument) {
    return (
      <Page title="Loading..." className="relative box-border shrink p-8">
        <DocumentDetailSkeleton />
      </Page>
    );
  }

  const document = docStore.currentDocument;

  const expired = docStore.isExpired(docStore.currentDocument);
  const canEdit = docStore.canEdit(docStore.currentDocument);
  const canUpload = docStore.canUpload();
  const canDownload = docStore.canDownload(docStore.currentDocument);
  const canDeleteDocument = docStore.canDeleteDocument(docStore.currentDocument);
  const hideManageButtons = docStore.isEBMSDocument(docStore.currentDocument);

  return (
    <Page title={title} className="relative box-border shrink p-8">
      {expired && <div className="document-error-banner">Expired</div>}
      {document.archived && <div className="document-error-banner">Archived</div>}
      <DocumentDeletionModal
        document={document}
        open={showDocumentDeletionDialog}
        close={() => setShowDocumentDeletionDialog(false)}
      />
      <DocumentArchiveModal
        document={document}
        open={showDocumentArchiveDialog}
        onSuccess={reloadDoc}
        close={() => setShowDocumentArchiveDialog(false)}
        unArchive={false}
      />
      <DocumentArchiveModal
        document={document}
        open={showDocumentRepublishDialog}
        onSuccess={reloadDoc}
        close={() => setShowDocumentRepublishDialog(false)}
        unArchive
      />
      <div className="relative mx-auto flex h-full w-full max-w-[1700px] justify-between gap-[30px]">
        <div className="grow-[2]">
          <div className="mb-6">
            <BackToSearchResultsButton />
          </div>
          <div className="relative overflow-hidden">
            <div className="pb-4 text-left">
              <div>
                <div className="mb-2 flex items-center">
                  <h3 className="m-0 inline-block text-[1.375rem] font-semibold leading-tight">{document.title}</h3>
                </div>
                <div>{document.abstract}</div>
              </div>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-1.5 text-xs">
              <ShareDocumentButton doc={document} />
              {canDownload && (
                <>
                  <DocumentDownloadButton doc={document} />
                  <DocBookmarkButton doc={document} />
                </>
              )}
            </div>

            <div className="max-w-[800px]">
              <div>
                <DocumentInfo document={docStore.currentDocument} />
              </div>
              <DocumentRelations related={document.related} />
            </div>
            <div className="flex-1" />
            {!hideManageButtons && (
              <div className="flex flex-wrap gap-1.5">
                {!!canEdit && (
                  <Button
                    variant="default"
                    onClick={() => navigate(paths.construct.editDocument(document.protocol))}
                  >
                    Edit
                  </Button>
                )}

                {!!canUpload && !!canEdit && (
                  <Link to={paths.construct.uploadSimilar(document.protocol)} rel="noopener noreferrer">
                    <Button variant="secondary">Upload similar</Button>
                  </Link>
                )}
                {!!canDeleteDocument && !!canEdit && (
                  <Button variant="destructive" onClick={startDeletion}>
                    Delete document
                  </Button>
                )}
                {!!canEdit && (
                  <>
                    {document.archived ? (
                      <Button
                        variant="destructive"
                        onClick={() => setShowDocumentRepublishDialog(true)}
                        disabled={!canDeleteDocument}
                      >
                        Re-publish document
                      </Button>
                    ) : (
                      <Button
                        variant="destructive"
                        onClick={() => setShowDocumentArchiveDialog(true)}
                        disabled={!canDeleteDocument}
                      >
                        Archive document
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex grow justify-end [&_img]:max-h-full [&_img]:max-w-full max-lg:hidden">
          <DocumentPreview doc={document} className="h-auto w-full max-w-[550px] min-w-[440px]" />
        </div>
      </div>
    </Page>
  );
});

export default DocumentDetailPage;
